/**
 * Sign-in: 3 options — (1) Email + password, (2) Email code (OTP sent by Entra native auth), (3) SSO (redirect).
 * Email code uses Entra native authentication API per https://learn.microsoft.com/en-us/entra/identity-platform/tutorial-native-authentication-single-page-app-react-sdk-sign-in
 */
import React, { useState } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { protectedResources, nativeAuthClientId, loginRequest } from "authConfig";
import { useNativeAuth } from "context/NativeAuthContext";

// Customer logo by sub-domain (e.g. qantas.dev… → /logos/qantas.png). Drop logo files in
// client/public/logos/<subdomain>.png to brand each tenant's sign-in screen. No rebuild needed.
const LOGIN_SUBDOMAIN = (typeof window !== "undefined" ? window.location.hostname : "")
  .split(".")[0]
  .toLowerCase();

// Per-environment kill switch for the tenant sign-in logo. UAT/prod builds set
// REACT_APP_HIDE_LOGIN_LOGO=true to suppress it while dev keeps the per-tenant branding.
const HIDE_LOGIN_LOGO = process.env.REACT_APP_HIDE_LOGIN_LOGO === "true";

const apiBase = protectedResources?.apiTodoList?.endpoint || process.env.REACT_APP_API_URL || "";
const clientId = nativeAuthClientId || "";

const SIGNIN_INITIATE = `${apiBase}/native-auth/signin/initiate`;
const SIGNIN_CHALLENGE = `${apiBase}/native-auth/signin/challenge`;
const SIGNIN_TOKEN = `${apiBase}/native-auth/signin/token`;

function friendlyAuthError(err) {
  const raw = err?.data?.error_description || err?.data?.error || err?.message || "";
  const code = raw.match(/AADSTS(\d+)/)?.[0];
  const map = {
    AADSTS50126: "Incorrect email or password. Please try again.",
    AADSTS50034: "Incorrect email or password. Please try again.",
    AADSTS50053: "Your account has been locked due to too many failed attempts. Try again later.",
    AADSTS50057: "Your account has been disabled. Please contact your administrator.",
    AADSTS50076: "Multi-factor authentication is required. Please use SSO to sign in.",
    AADSTS50079: "Multi-factor authentication is required. Please use SSO to sign in.",
    AADSTS90072: "This account is not authorised to access this application.",
    AADSTS70011: "The sign-in request is invalid. Please refresh and try again.",
  };
  if (code && map[code]) return map[code];
  // Strip trace IDs and timestamps from unrecognised Entra errors
  if (raw.includes("AADSTS")) {
    const clean = raw
      .replace(/Trace ID:.*$/im, "")
      .replace(/Correlation ID:.*$/im, "")
      .replace(/Timestamp:.*$/im, "")
      .replace(/\n+/g, " ")
      .trim();
    return clean || "Sign-in failed. Please try again.";
  }
  return raw || "Sign-in failed.";
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let data;
  try {
    data = await res.json();
  } catch {
    data = { error_description: await res.text() };
  }
  if (!res.ok) {
    throw Object.assign(new Error(data.error_description || data.error || res.statusText), {
      data,
      status: res.status,
    });
  }
  return data;
}

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const { setTokens } = useNativeAuth();
  // Only email + password sign-in is shown by default; the email-code/SSO flows remain in the
  // code (behind this fixed method) so they can be re-enabled later without rework.
  const [method] = useState("password"); // "password" | "email_code" | "sso"
  const [logoOk, setLogoOk] = useState(true); // customer logo present for this sub-domain?
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Email-code flow (Entra native auth): request → Entra sends OTP → verify
  const [emailCodeStep, setEmailCodeStep] = useState("request"); // "request" | "verify"
  const [otp, setOtp] = useState("");
  const [codeLength, setCodeLength] = useState(8);
  const [continuationTokenOob, setContinuationTokenOob] = useState("");
  const [challengeTargetLabel, setChallengeTargetLabel] = useState("");

  const finishSignIn = (tokenRes) => {
    setTokens({
      access_token: tokenRes.access_token,
      refresh_token: tokenRes.refresh_token || "",
      id_token: tokenRes.id_token || tokenRes.access_token,
      expires_in: tokenRes.expires_in || 3600,
    });
    // Return the user to the page they were headed to (set by AuthGuard when a lapsed session bounced
    // them here), else the home dashboard.
    const dest = location.state?.from;
    navigate(dest && dest.startsWith("/") && !dest.startsWith("/signin") ? dest : "/", {
      replace: true,
    });
  };

  // ——— 1) Password (native auth) ———
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const startRes = await postJson(SIGNIN_INITIATE, {
        client_id: clientId,
        username: email.trim(),
        challenge_type: "password oob redirect",
      });
      if (startRes.challenge_type === "redirect") {
        instance.loginRedirect(loginRequest).catch(() => setLoading(false));
        return;
      }
      const token = startRes.continuation_token;
      if (!token) {
        setError("Could not start sign-in. Try again.");
        setLoading(false);
        return;
      }
      const challengeRes = await postJson(SIGNIN_CHALLENGE, {
        client_id: clientId,
        continuation_token: token,
        challenge_type: "password oob redirect",
      });
      if (challengeRes.challenge_type === "redirect") {
        instance.loginRedirect(loginRequest).catch(() => setLoading(false));
        return;
      }
      const continuationToken = challengeRes.continuation_token;
      if (!continuationToken) {
        setError(challengeRes.error_description || "Unexpected response from sign-in.");
        setLoading(false);
        return;
      }
      if (challengeRes.challenge_type === "oob") {
        setError(
          "Your account requires a one-time email code to sign in. Please contact your administrator."
        );
        setLoading(false);
        return;
      }
      const tokenRes = await postJson(SIGNIN_TOKEN, {
        client_id: clientId,
        continuation_token: continuationToken,
        grant_type: "password",
        password,
        scope: "openid offline_access",
      });
      finishSignIn(tokenRes);
    } catch (err) {
      setError(friendlyAuthError(err));
    }
    setLoading(false);
  };

  // ——— 2) Email code (Entra native auth: Entra sends OTP to email) ———
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Enter your email address.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const startRes = await postJson(SIGNIN_INITIATE, {
        client_id: clientId,
        username: email.trim(),
        challenge_type: "oob redirect",
      });
      if (startRes.challenge_type === "redirect") {
        setError(
          "Your account doesn't support email code sign-in. Please use the Password or SSO tab instead."
        );
        setLoading(false);
        return;
      }
      const token = startRes.continuation_token;
      if (!token) {
        setError(startRes.error_description || "Could not start sign-in. Try again.");
        setLoading(false);
        return;
      }
      const challengeRes = await postJson(SIGNIN_CHALLENGE, {
        client_id: clientId,
        continuation_token: token,
        challenge_type: "oob redirect",
      });
      if (challengeRes.challenge_type === "redirect") {
        setError(
          "Your account doesn't support email code sign-in. Please use the Password or SSO tab instead."
        );
        setLoading(false);
        return;
      }
      const continuationToken = challengeRes.continuation_token;
      if (!continuationToken) {
        setError(challengeRes.error_description || "Unexpected response. Try password or SSO.");
        setLoading(false);
        return;
      }
      if (challengeRes.challenge_type === "oob") {
        setContinuationTokenOob(continuationToken);
        setChallengeTargetLabel(challengeRes.challenge_target_label || email.trim());
        setCodeLength(challengeRes.code_length || 8);
        setEmailCodeStep("verify");
      } else {
        setError(challengeRes.error_description || "Unexpected response. Try password or SSO.");
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!continuationTokenOob || !otp.trim()) {
      setError("Enter the code we sent to your email.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const tokenRes = await postJson(SIGNIN_TOKEN, {
        client_id: clientId,
        continuation_token: continuationTokenOob,
        grant_type: "oob",
        oob: otp.trim(),
        scope: "openid offline_access",
      });
      finishSignIn(tokenRes);
    } catch (err) {
      setError(friendlyAuthError(err));
    }
    setLoading(false);
  };

  // ——— 3) SSO (redirect) ———
  const handleSso = () => {
    setError(null);
    instance
      .loginRedirect(loginRequest)
      .catch((err) => setError(err?.message || "SSO sign-in failed."));
  };

  return (
    <MDBox pt={4} px={2} maxWidth={480} mx="auto">
      {/* Customer logo by sub-domain (e.g. qantas.dev… → /logos/qantas.png). Hidden if no file exists,
          or suppressed entirely in UAT/prod via REACT_APP_HIDE_LOGIN_LOGO. */}
      {!HIDE_LOGIN_LOGO && logoOk && LOGIN_SUBDOMAIN && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <img
            src={`/logos/${LOGIN_SUBDOMAIN}.png`}
            alt="Company logo"
            style={{ maxHeight: 72, maxWidth: 240, objectFit: "contain" }}
            onError={() => setLogoOk(false)}
          />
        </Box>
      )}

      <Typography variant="h5" fontWeight="medium" gutterBottom>
        Sign in
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 1) Password */}
      {method === "password" && (
        <Box component="form" onSubmit={handlePasswordSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
            // Keep label floated so browser-autofilled values don't overlap it
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="current-password"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mt: 3 }}>
            <MDButton type="submit" variant="gradient" color="info" disabled={loading} fullWidth>
              {loading ? "Signing in…" : "Sign in"}
            </MDButton>
          </Box>
        </Box>
      )}

      {/* 2) Email code */}
      {method === "email_code" && (
        <Box>
          {emailCodeStep === "request" ? (
            <Box component="form" onSubmit={handleSendCode}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email. We’ll send a one-time code to your inbox (Entra native
                authentication).
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
              />
              <Box sx={{ mt: 3 }}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={loading}
                  fullWidth
                >
                  {loading ? "Sending code…" : "Send code"}
                </MDButton>
              </Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerifyCode}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We sent a one-time code to {challengeTargetLabel || email}. Enter it below.
              </Typography>
              <TextField
                fullWidth
                label="Verification code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, codeLength))}
                margin="normal"
                required
                placeholder="000000"
                inputProps={{ maxLength: codeLength, inputMode: "numeric" }}
              />
              <Box sx={{ mt: 3 }}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={loading}
                  fullWidth
                >
                  {loading ? "Verifying…" : "Verify and sign in"}
                </MDButton>
              </Box>
              <Typography
                variant="body2"
                sx={{ mt: 2 }}
                color="text.secondary"
                component="button"
                type="button"
                onClick={() => {
                  setEmailCodeStep("request");
                  setOtp("");
                  setContinuationTokenOob("");
                  setError(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Use a different email or resend code
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* 3) SSO */}
      {method === "sso" && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sign in with your organization’s account (Microsoft / Entra). You’ll be redirected to
            the sign-in page.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSso}
            disabled={loading}
            startIcon={<LoginIcon />}
          >
            Sign in with SSO
          </Button>
        </Box>
      )}

      <Typography variant="body2" sx={{ mt: 3 }} color="text.secondary">
        Don’t have an account?{" "}
        <RouterLink to="/signup" style={{ color: "inherit", fontWeight: 500 }}>
          Sign up
        </RouterLink>
      </Typography>
    </MDBox>
  );
}
